import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Snackbar,
  Alert,
  Box,
  Chip,
  Paper,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  CloudDone as CloudDoneIcon,
  Calculate as CalculateIcon,
  Warning as WarningIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { API_URL, MAX_RUBRIC_SCORE } from '../config';
import { calculateGroupScore } from '../lib/calculateGroupScore';
import { validateGroupData } from '../lib/validateGroupData';
import { StudentManager } from '../components/StudentManager';
import { GeneralFeedback } from '../components/GeneralFeedback';
import { GradingTable } from '../components/grading/GradingTable';

export function GraderView({ auth, onSave }) {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [currentGroup, setCurrentGroup] = useState(null);
  const [lastSyncedGroup, setLastSyncedGroup] = useState(null);
  const groupRef = useRef(null);
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });
  const [lastSaved, setLastSaved] = useState(null);
  const [editGroupOpen, setEditGroupOpen] = useState(false);
  const [editGroupDraft, setEditGroupDraft] = useState({ groupNumber: '', groupName: '' });

  const currentGroupJson = useMemo(
    () => (currentGroup ? JSON.stringify({ ...currentGroup, updatedAt: null }) : ''),
    [currentGroup]
  );
  const lastSyncedJson = useMemo(
    () => (lastSyncedGroup ? JSON.stringify({ ...lastSyncedGroup, updatedAt: null }) : ''),
    [lastSyncedGroup]
  );
  const isDirty = currentGroupJson !== '' && lastSyncedJson !== '' && currentGroupJson !== lastSyncedJson;

  const checkIsDirtyRef = useRef(() => false);
  checkIsDirtyRef.current = () => {
    if (!groupRef.current || !lastSyncedGroup) return false;
    const a = JSON.stringify({ ...groupRef.current, updatedAt: null });
    const b = JSON.stringify({ ...lastSyncedGroup, updatedAt: null });
    return a !== b;
  };

  const fetchGroupData = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/groups/${groupId}`, {
        headers: { 'x-session-token': auth.token },
      });
      if (res.ok) {
        const data = await res.json();
        setCurrentGroup(data);
        setLastSyncedGroup(JSON.parse(JSON.stringify(data)));
      }
    } catch (err) {
      console.error('Fetch failed', err);
    }
  }, [groupId, auth.token]);

  const handleLocalSave = async (silent = false) => {
    const data = groupRef.current;
    if (!data) return;
    await onSave(data, silent, (msg, type, savedData) => {
      if (!silent) setToast({ open: true, msg, severity: type });
      if (type === 'success' && savedData) {
        setLastSyncedGroup(JSON.parse(JSON.stringify(savedData)));
        setCurrentGroup(savedData);
        setLastSaved(new Date().toLocaleTimeString());
      }
    });
  };

  const handleLocalSaveRef = useRef(handleLocalSave);
  handleLocalSaveRef.current = handleLocalSave;

  useEffect(() => {
    fetchGroupData();
  }, [fetchGroupData]);

  useEffect(() => {
    const onFocus = () => {
      if (!isDirty) fetchGroupData();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [isDirty, fetchGroupData]);

  useEffect(() => {
    groupRef.current = currentGroup;
  }, [currentGroup]);

  useEffect(() => {
    if (!currentGroup) return;
    const timer = setInterval(() => {
      if (groupRef.current && checkIsDirtyRef.current()) handleLocalSaveRef.current(true);
    }, 10000);
    return () => clearInterval(timer);
  }, [currentGroup, lastSyncedGroup]);

  const handleCloseToast = () => setToast((t) => ({ ...t, open: false }));

  const openEditGroup = () => {
    setEditGroupDraft({
      groupNumber: currentGroup.groupNumber || '',
      groupName: currentGroup.groupName || '',
    });
    setEditGroupOpen(true);
  };

  const confirmEditGroup = () => {
    setCurrentGroup((prev) => ({
      ...prev,
      groupNumber: editGroupDraft.groupNumber,
      groupName: editGroupDraft.groupName,
    }));
    setEditGroupOpen(false);
  };

  const updateField = useCallback((sectionKey, index, field, value) => {
    setCurrentGroup((prev) => {
      const newSection = [...prev[sectionKey]];
      newSection[index] = { ...newSection[index], [field]: value === '' ? null : value };
      return { ...prev, [sectionKey]: newSection };
    });
  }, []);

  const updateBasicField = useCallback(
    (idx, field, v) => updateField('basicFeatures', idx, field, v),
    [updateField]
  );
  const updateDeployField = useCallback(
    (idx, field, v) => updateField('deployment', idx, field, v),
    [updateField]
  );
  const updateBonusField = useCallback(
    (idx, field, v) => updateField('bonus', idx, field, v),
    [updateField]
  );

  const currentScore = useMemo(
    () => (currentGroup ? calculateGroupScore(currentGroup) : 0),
    [currentGroup]
  );
  const validationError = useMemo(
    () => (currentGroup ? validateGroupData(currentGroup) : null),
    [currentGroup]
  );
  const isGroupValid = validationError === null;

  if (!currentGroup) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }
  const isMyGroup = currentGroup.createdBy === auth.username;

  return (
    <div style={{ backgroundColor: '#f4f6f8', minHeight: '100vh' }}>
      <Box sx={{ pb: 10 }}>
        <AppBar position="sticky" color="default" elevation={1}>
          <Toolbar>
            <IconButton edge="start" onClick={() => navigate('/')} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Box sx={{ flexGrow: 1 }}>
              <Box display="flex" alignItems="center">
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  Group {currentGroup.groupNumber}{' '}
                  <span style={{ fontSize: '0.8em', color: '#666' }}> ({currentGroup.groupName})</span>
                </Typography>
                {isMyGroup && (
                  <IconButton size="small" onClick={openEditGroup} sx={{ ml: 0.5 }}>
                    <EditIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                )}
              </Box>
              <Box display="flex" gap={2} alignItems="center">
                {lastSaved && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                    <CloudDoneIcon sx={{ fontSize: 14, mr: 0.5 }} /> Saved {lastSaved}
                  </Typography>
                )}
                <Typography
                  variant="caption"
                  color={isMyGroup ? 'primary' : 'text.secondary'}
                  fontWeight={isMyGroup ? 'bold' : 'normal'}
                >
                  Managed by: {currentGroup.createdBy || 'Unknown'} {isMyGroup ? '(You)' : ''}
                </Typography>
              </Box>
            </Box>
            {isDirty && <Chip label="Unsaved Changes" size="small" color="warning" sx={{ mr: 2 }} />}
            <Paper
              elevation={0}
              sx={{
                px: 2,
                py: 1,
                mr: 2,
                bgcolor: isGroupValid ? '#e8f5e9' : '#fff3e0',
                border: isGroupValid ? '1px solid #c8e6c9' : '1px solid #ffb74d',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              {isGroupValid ? (
                <>
                  <CalculateIcon color="success" sx={{ mr: 1 }} />
                  <Typography variant="h6" color="success.main" fontWeight="bold">
                    {currentScore.toFixed(1)} / {MAX_RUBRIC_SCORE}
                  </Typography>
                </>
              ) : (
                <>
                  <WarningIcon color="warning" sx={{ mr: 1 }} />
                  <Typography variant="body1" color="warning.dark" fontWeight="bold">
                    Invalid Grades
                  </Typography>
                </>
              )}
            </Paper>
            <Button
              variant="contained"
              color={isGroupValid ? 'primary' : 'warning'}
              startIcon={<SaveIcon />}
              onClick={() => handleLocalSave(false)}
            >
              Save
            </Button>
          </Toolbar>
        </AppBar>
        <Container maxWidth="xl" sx={{ mt: 4 }}>
          {!isGroupValid && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Errors detected. Data saved, but score invalid.
            </Alert>
          )}
          {!isMyGroup && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Read-Only Mode: You cannot save changes to {currentGroup.createdBy}&apos;s group.
            </Alert>
          )}
          <StudentManager currentGroup={currentGroup} onUpdate={setCurrentGroup} />
          <GradingTable
            title="1. Basic Features"
            sectionKey="basicFeatures"
            data={currentGroup.basicFeatures}
            onFieldChange={updateBasicField}
          />
          <GradingTable
            title="2. Deployment & Reliability"
            sectionKey="deployment"
            data={currentGroup.deployment}
            onFieldChange={updateDeployField}
          />
          <GradingTable
            title="3. Bonus (optional)"
            sectionKey="bonus"
            data={currentGroup.bonus || []}
            onFieldChange={updateBonusField}
          />
          <GeneralFeedback group={currentGroup} onUpdate={setCurrentGroup} />
        </Container>
      </Box>
      <Snackbar open={toast.open} autoHideDuration={6000} onClose={handleCloseToast}>
        <Alert onClose={handleCloseToast} severity={toast.severity} sx={{ width: '100%' }}>
          {toast.msg}
        </Alert>
      </Snackbar>

      <Dialog open={editGroupOpen} onClose={() => setEditGroupOpen(false)}>
        <DialogTitle>Edit Group Info</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Group Number"
            fullWidth
            variant="outlined"
            value={editGroupDraft.groupNumber}
            onChange={(e) => setEditGroupDraft((d) => ({ ...d, groupNumber: e.target.value }))}
          />
          <TextField
            margin="dense"
            label="Group Name"
            fullWidth
            variant="outlined"
            value={editGroupDraft.groupName}
            onChange={(e) => setEditGroupDraft((d) => ({ ...d, groupName: e.target.value }))}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGroupOpen(false)}>Cancel</Button>
          <Button
            onClick={confirmEditGroup}
            variant="contained"
            disabled={!editGroupDraft.groupNumber.trim() || !editGroupDraft.groupName.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
}
